import React from "react";
import { Button, message } from "antd";
import { Link } from "react-router-dom";
import {
  InstagramOutlined,
  TwitterOutlined,
  FacebookFilled,
  PlusOutlined,
} from "@ant-design/icons";
import { del } from "../../api/axios";
import { checkPermission } from "../auth/ProtectedRoutes";
import useTableData from "../../utils/useTableData";
import DataTable, { editAction, deleteAction } from "../../utils/DataTable";
import { socialLink } from "../../utils/tableFields";

/**
 * مصدر واحد لتعريف الأعمدة: يُستخدم للجدول، وللصف الموسّع، وللتصدير.
 * key       : يطابق اسم العمود الذي يفهمه الخادم للترتيب (sort_by)
 * priority  : الأهم أولاً — الأعمدة الأقل أهمية تنتقل داخل علامة (+) عند ضيق الشاشة
 * value     : القيمة الخام (تُستخدم في التصدير وفي العرض الافتراضي)
 * render    : عرض مخصّص داخل الجدول عند الحاجة
 * sortable  : false للأعمدة التي لا يدعم الخادم ترتيبها
 */
const FIELDS = [
  { key: "id", title: "الرقم", width: 80, priority: 1, value: (r) => r.id },
  { key: "name", title: "الاسم", width: 170, priority: 2, value: (r) => r.name },
  { key: "phone", title: "الجوال", width: 140, priority: 3, value: (r) => r.phone },
  { key: "job_title", title: "المسمى الوظيفي", width: 150, priority: 4, value: (r) => r.job_title },
  { key: "email", title: "البريد الإلكتروني", width: 210, priority: 5, value: (r) => r.email },
  { key: "role", title: "الأذونات", width: 130, priority: 6, sortable: false, value: (r) => r.role },
  { key: "salary", title: "الراتب", width: 110, priority: 7, value: (r) => r.salary },
  { key: "nationality_id", title: "الجنسية", width: 130, priority: 8, value: (r) => r.nationality_id },
  { key: "created_at", title: "تاريخ الاضافة", width: 130, priority: 9, value: (r) => r.created_at },
  { key: "code", title: "رقم الموظف", width: 120, priority: 10, value: (r) => r.code },
  { key: "id_number", title: "رقم الاثبات", width: 140, priority: 11, value: (r) => r.id_number },
  { key: "residence_type", title: "الإقامة", width: 120, priority: 12, value: (r) => r.residence_type },
  { key: "address", title: "العنوان", width: 150, priority: 13, value: (r) => r.address },
  { key: "dob", title: "تاريخ الميلاد", width: 130, priority: 14, value: (r) => r.dob },
  { key: "gender", title: "الجنس", width: 100, priority: 15, value: (r) => r.gender },
  { key: "marital_status", title: "الحالة الاجتماعية", width: 150, priority: 16, value: (r) => r.marital_status },
  { key: "contact_start_date", title: "تاريخ التعاقد", width: 130, priority: 17, value: (r) => r.contact_start_date },
  { key: "contact_end_date", title: "تاريخ انتهاء التعاقد", width: 160, priority: 18, value: (r) => r.contact_end_date },
  { key: "employment_source", title: "مصدر التوظيف", width: 150, priority: 19, value: (r) => r.employment_source },
  { key: "qualification", title: "المؤهل", width: 130, priority: 20, value: (r) => r.qualification },
  { key: "specialization", title: "التخصص", width: 150, priority: 21, value: (r) => r.specialization },
  { key: "source_of_certificate", title: "مصدر الشهادة", width: 150, priority: 22, value: (r) => r.source_of_certificate },
  {
    key: "country_of_certificate",
    title: "دولة المؤهل",
    width: 140,
    priority: 23,
    value: (r) => r.country_of_certificate?.name_ar,
  },
  { key: "graduation_date", title: "تاريخ التخرج", width: 130, priority: 24, value: (r) => r.graduation_date },
  { key: "rate", title: "التقدير", width: 120, priority: 25, value: (r) => r.rate?.name },
  { key: "social_insurance", title: "التأمينات الاجتماعية", width: 170, priority: 26, value: (r) => r.social_insurance },
  { key: "insurance_discount", title: "خصم التأمينات", width: 140, priority: 27, value: (r) => r.insurance_discount },
  {
    key: "instagram_url",
    title: "الانستغرام",
    width: 110,
    priority: 28,
    sortable: false,
    value: (r) => r.instagram_url,
    render: (r) => socialLink(r.instagram_url, <InstagramOutlined style={{ fontSize: "20px" }} />),
  },
  {
    key: "twitter_url",
    title: "تويتر",
    width: 100,
    priority: 29,
    sortable: false,
    value: (r) => r.twitter_url,
    render: (r) => socialLink(r.twitter_url, <TwitterOutlined style={{ fontSize: "20px" }} />),
  },
  {
    key: "facebook_url",
    title: "فيسبوك",
    width: 100,
    priority: 30,
    sortable: false,
    value: (r) => r.facebook_url,
    render: (r) => socialLink(r.facebook_url, <FacebookFilled style={{ fontSize: "20px" }} />),
  },
];

const Employees = () => {
  const table = useTableData("employees");

  const handleDelete = async ({ id }) => {
    try {
      await del(`employees/${id}`);
      table.removeRow(id);
      message.success("تم الامر بنجاح");
    } catch (error) {
      message.error("تعذّر حذف الموظف");
    }
  };

  const actions = [];

  if (checkPermission("update-employee")) {
    actions.push(editAction(({ id }) => `/employees/edit/${id}`));
  }

  if (checkPermission("delete-employee")) {
    actions.push(deleteAction(handleDelete));
  }

  return (
    <DataTable
      table={table}
      fields={FIELDS}
      actions={actions}
      exportName="employees"
      itemLabel="موظف"
      searchPlaceholder="ابحث بالاسم أو البريد أو الجوال..."
      toolbarActions={
        checkPermission("create-employee") && (
          <Link className="text-decoration-none" to="/employees/add">
            <Button type="primary" icon={<PlusOutlined />}>
              إضافة موظف
            </Button>
          </Link>
        )
      }
    />
  );
};

export default Employees;
